import { computed, ref } from "vue";
import { refDebounced } from "@vueuse/core";
import type { BannedEmailSource, BannedIpSource } from "@shopexperts/legacy-api";
import { useBanRuleMutations, useBanRules } from "./useBanRules";
import { useBannedEmailMutations, useBannedEmails } from "./useBannedEmails";
import { useBannedIpMutations, useBannedIps } from "./useBannedIps";
import { KNOWN_RULE_TYPE_OPTIONS } from "./banListForms";

export type BanRuleActiveFilter = "all" | "active" | "inactive";
export type BanListTab = "rules" | "emails" | "ips";

/** UiSelect models strings, so "" is the "no filter" sentinel throughout. */
const ANY = "";

export function useBanListPage() {
    const activeTab = ref<BanListTab>("rules");

    const ruleSearchInput = ref("");
    const ruleTypeFilter = ref<string>(ANY);
    const ruleActiveFilter = ref<BanRuleActiveFilter>("all");
    const debouncedRuleSearch = refDebounced(ruleSearchInput, 300);

    const emailSearchInput = ref("");
    const emailSourceFilter = ref<BannedEmailSource | typeof ANY>(ANY);
    const emailRuleFilter = ref<string>(ANY);
    const debouncedEmailSearch = refDebounced(emailSearchInput, 300);

    const ipSearchInput = ref("");
    const ipSourceFilter = ref<BannedIpSource | typeof ANY>(ANY);
    const ipRuleFilter = ref<string>(ANY);
    const debouncedIpSearch = refDebounced(ipSearchInput, 300);

    const rulesQuery = computed(() => ({
        search: debouncedRuleSearch.value.trim() || undefined,
        type: ruleTypeFilter.value || undefined,
        is_active: ruleActiveFilter.value === "all" ? undefined : ruleActiveFilter.value === "active",
    }));

    const emailsQuery = computed(() => ({
        search: debouncedEmailSearch.value.trim() || undefined,
        source: emailSourceFilter.value || undefined,
        ban_rule_id: toRuleId(emailRuleFilter.value),
    }));

    const ipsQuery = computed(() => ({
        search: debouncedIpSearch.value.trim() || undefined,
        source: ipSourceFilter.value || undefined,
        ban_rule_id: toRuleId(ipRuleFilter.value),
    }));

    const rulesResult = useBanRules(rulesQuery);
    const emailsResult = useBannedEmails(emailsQuery);
    const ipsResult = useBannedIps(ipsQuery);

    const rules = computed(() => rulesResult.data.value?.pages.flatMap((page) => page.data) ?? []);
    const bannedEmails = computed(() => emailsResult.data.value?.pages.flatMap((page) => page.data) ?? []);
    const bannedIps = computed(() => ipsResult.data.value?.pages.flatMap((page) => page.data) ?? []);

    /**
     * Rule pickers must not be fed by the filtered list — that would hide rules
     * an admin still needs to select — so they get their own unfiltered query.
     * Capped at the API's maximum page size; beyond that, filter by rule from a
     * rule card instead of the dropdown.
     */
    const allRulesResult = useBanRules({ limit: 100 });
    const allRules = computed(() => allRulesResult.data.value?.pages.flatMap((page) => page.data) ?? []);

    const ruleOptions = computed(() => allRules.value.map((rule) => ({
        label: rule.name,
        value: String(rule.id),
    })));

    /** Merges registry types the UI knows about with types already in use. */
    const ruleTypeOptions = computed(() => {
        const seen = new Map<string, { label: string; value: string }>();
        for (const option of KNOWN_RULE_TYPE_OPTIONS) {
            seen.set(option.value, { ...option });
        }
        for (const rule of allRules.value) {
            if (!seen.has(rule.type)) {
                seen.set(rule.type, { label: rule.type, value: rule.type });
            }
        }
        return [...seen.values()];
    });

    const loadedRulesSummary = computed(() => {
        const loaded = rules.value;
        return {
            total: loaded.length,
            active: loaded.filter((rule) => rule.is_active).length,
            hits: loaded.reduce((sum, rule) => sum + rule.hits_count, 0),
            hasMore: Boolean(rulesResult.hasNextPage.value),
        };
    });

    const hasRuleFilters = computed(() => Boolean(
        rulesQuery.value.search || rulesQuery.value.type || rulesQuery.value.is_active !== undefined,
    ));
    const hasEmailFilters = computed(() => Boolean(
        emailsQuery.value.search || emailsQuery.value.source || emailsQuery.value.ban_rule_id,
    ));
    const hasIpFilters = computed(() => Boolean(
        ipsQuery.value.search || ipsQuery.value.source || ipsQuery.value.ban_rule_id,
    ));

    function resetRuleFilters() {
        ruleSearchInput.value = "";
        ruleTypeFilter.value = ANY;
        ruleActiveFilter.value = "all";
    }

    function resetEmailFilters() {
        emailSearchInput.value = "";
        emailSourceFilter.value = ANY;
        emailRuleFilter.value = ANY;
    }

    function resetIpFilters() {
        ipSearchInput.value = "";
        ipSourceFilter.value = ANY;
        ipRuleFilter.value = ANY;
    }

    /** Jumps from a rule to the records it produced. */
    function showEmailsForRule(ruleId: number) {
        resetEmailFilters();
        emailRuleFilter.value = String(ruleId);
        activeTab.value = "emails";
    }

    function showIpsForRule(ruleId: number) {
        resetIpFilters();
        ipRuleFilter.value = String(ruleId);
        activeTab.value = "ips";
    }

    return {
        activeTab,

        ruleSearchInput,
        ruleTypeFilter,
        ruleActiveFilter,
        rules,
        rulesResult,
        loadedRulesSummary,
        hasRuleFilters,
        resetRuleFilters,

        emailSearchInput,
        emailSourceFilter,
        emailRuleFilter,
        bannedEmails,
        emailsResult,
        hasEmailFilters,
        resetEmailFilters,

        ipSearchInput,
        ipSourceFilter,
        ipRuleFilter,
        bannedIps,
        ipsResult,
        hasIpFilters,
        resetIpFilters,

        ruleOptions,
        ruleTypeOptions,
        showEmailsForRule,
        showIpsForRule,

        ...useBanRuleMutations(),
        ...useBannedEmailMutations(),
        ...useBannedIpMutations(),
    };
}

function toRuleId(value: string): number | undefined {
    if (!value) return undefined;
    const parsed = Number.parseInt(value, 10);
    return Number.isNaN(parsed) ? undefined : parsed;
}
